import { useAtomValue, useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { createBreadcrumbWithI18n } from '~/apps/ninesegsAnalyzer/routes/paths'
import { pathKey } from '~/apps/ninesegsAnalyzer/utils/standardAnalysis'
import { Box } from '~/components/elements/Box'
import { HStack } from '~/components/elements/HStack'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import { VStack } from '~/components/elements/VStack'
import { CustomerAttributeTreeViewBox } from '~/components/features/CustomerAttributeTreeViewBox'
import { DataDisplayTypeAtomSwitcher } from '~/components/features/DataDisplayTypeAtomSwitcher'
import { QuestionTreeViewBox } from '~/components/features/QuestionTreeViewBox'
import { SegmentDisplaySwitcher } from '~/components/features/SegmentDisplaySwitcher'
import { SharedBrandGroupSelector } from '~/components/features/SharedBrandGroupSelector'
import { useRestoreOnMount } from '~/hooks/useRestoreOnMount'
import { useScrollEffect } from '~/hooks/useScrollEffect'
import { useSurveyQueryFlattenCustomerAttributes } from '~/hooks/useSurveyQuery'
import { exists } from '~/utils'

import {
  customerAttributeIdsAtom,
  dataDisplayTypeAtom,
  isPendingTabulationAtom,
  isTabulationReadyAtom,
  questionIdsAtom,
  segmentDisplayTypeAtom,
  tabulationQueryDataAtom,
} from '../atoms'
import { useRestoreParams } from '../hooks/restoringParams/useRestoreParams'
import { useSurveyId } from '../hooks/useSurveyId'
import { useTabulationQuery } from '../hooks/useTabulationQuery'

import { BrandSelectorContainer } from './BrandSelectorContainer'
import { Outputs } from './Outputs'
import { SelectedBrandList } from './SelectedBrandList'
import styles from './StandardAnalysis.css'
import { TabulateButton } from './TabulateButton'

export const StandardAnalysis = () => {
  const surveyId = useSurveyId()
  const { t } = useTranslation()

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('basicAnalysis'),
      createBreadcrumb(pathKey, { surveyId }),
    ],
    [createBreadcrumb, surveyId],
  )

  const isExecuting = useTabulationQuery()

  const customerAttributes = useSurveyQueryFlattenCustomerAttributes()

  const setIsPendingTabulation = useSetAtom(isPendingTabulationAtom)

  const suspendTabulation = React.useCallback(() => {
    setIsPendingTabulation(true)
  }, [setIsPendingTabulation])

  const restoreParams = useRestoreParams()
  useRestoreOnMount(restoreParams, isTabulationReadyAtom)

  const tabulationQueryData = useAtomValue(tabulationQueryDataAtom)
  useScrollEffect(exists(tabulationQueryData))

  return (
    <PageContainer breadcrumbs={breadcrumbs} executing={isExecuting}>
      <VStack spacing={0} verticalAppearance="stretch-scroll">
        <Box id="top" />

        <VStack spacing={8}>
          <VStack spacing={4}>
            <LabelingContainer label={t('brand_group')}>
              <SharedBrandGroupSelector />
            </LabelingContainer>

            <LabelingContainer label={t('switch_view_6')}>
              <HStack spacing={4}>
                <DataDisplayTypeAtomSwitcher
                  dataDisplayTypeAtom={dataDisplayTypeAtom}
                />

                <SegmentDisplaySwitcher
                  segmentDisplayTypeAtom={segmentDisplayTypeAtom}
                />
              </HStack>
            </LabelingContainer>

            <LabelingContainer label={t('select_brand')}>
              <VStack spacing={1}>
                <SelectedBrandList />

                <BrandSelectorContainer />
              </VStack>
            </LabelingContainer>

            <LabelingContainer label={t('select_questionnaire')}>
              <QuestionTreeViewBox
                questionIdsAtom={questionIdsAtom}
                onSelectedQuestionIdChange={suspendTabulation}
              />
            </LabelingContainer>

            {0 < customerAttributes.length && (
              <LabelingContainer label={t('customized_segments_selection')}>
                <CustomerAttributeTreeViewBox
                  customerAttributeIdsAtom={customerAttributeIdsAtom}
                  onSelectedCustomerAttributeIdsChange={suspendTabulation}
                />
              </LabelingContainer>
            )}

            <TabulateButton className={styles.tabulateButton} />
          </VStack>

          <Outputs />
        </VStack>
      </VStack>
    </PageContainer>
  )
}
